import { MinusIcon, PlusIcon } from 'lucide-react'
import { createContext, useContext, useRef } from 'react'

import { cn } from '~/utils/classnames'
import { composeRefs } from '~/utils/react'

import { IconButton } from './button'
import { Spinner } from './spinner'

const CounterContext = createContext<{
  inputRef: React.RefObject<HTMLInputElement | null>
  disabled?: boolean
}>({ inputRef: { current: null }, disabled: false })

const CounterRoot = ({
  ref,
  invalid,
  disabled,
  className,
  children,
  isLoading,
  ...props
}: React.ComponentPropsWithRef<'div'> & {
  invalid?: boolean
  disabled?: boolean
  isLoading?: boolean
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  return (
    <div
      inert={disabled || isLoading}
      data-disabled={disabled}
      data-loading={isLoading}
      data-invalid={invalid}
      aria-invalid={invalid}
      className={cn(
        'p-0.5',
        'bg-under/2 text-foreground hover:text-foreground/80 text-sm',
        'flex items-center justify-between gap-1',
        'rounded-xl',
        'border-border/6 border',
        'ring-accent-element transition focus-within:ring-4',
        'hover:bg-under/4',
        'data-[disabled=true]:border-border/6 data-[disabled=true]:bg-under/2 data-[disabled=true]:opacity-48',
        'data-[invalid=true]:border-destructive data-[invalid=true]:ring-destructive/20!',
        className
      )}
      {...props}
    >
      <IconButton
        type="button"
        className="text-inherit"
        size="sm"
        disabled={disabled}
        variant="ghost"
        aria-label="Decrease"
        onClick={() => {
          inputRef.current?.stepDown()
          inputRef.current?.dispatchEvent(
            new Event('change', { bubbles: true })
          )
        }}
      >
        <MinusIcon />
      </IconButton>

      {isLoading && (
        <Spinner
          className="absolute top-1/2 left-1/2 -translate-1/2"
          size="sm"
        />
      )}

      <CounterContext.Provider value={{ inputRef, disabled }}>
        <div
          className={cn(
            'flex flex-1 items-center justify-center gap-1',
            isLoading && 'opacity-0'
          )}
        >
          {children || <CounterInput />}
        </div>
      </CounterContext.Provider>

      <IconButton
        type="button"
        className="text-inherit"
        size="sm"
        disabled={disabled}
        variant="ghost"
        aria-label="Increase"
        onClick={() => {
          inputRef.current?.stepUp()
          inputRef.current?.dispatchEvent(
            new Event('change', { bubbles: true })
          )
        }}
      >
        <PlusIcon />
      </IconButton>
    </div>
  )
}

const CounterInput = ({
  ref,
  className,
  disabled,
  defaultValue,
  ...props
}: Omit<React.ComponentPropsWithRef<'input'>, 'type'>) => {
  const ctx = useContext(CounterContext)

  if (!ctx) {
    throw new Error('CounterInput must be used within a Counter')
  }

  return (
    <input
      ref={composeRefs(ref, ctx.inputRef)}
      type="number"
      className={cn(
        'text-foreground focus:bg-accent-element/24 box-content w-[3ch] rounded-xs bg-transparent p-0.5 text-center text-sm leading-none text-ellipsis whitespace-nowrap tabular-nums outline-none focus:ring-0 md:text-xs',
        className
      )}
      disabled={ctx.disabled || disabled}
      defaultValue={defaultValue || '0'}
      {...props}
    />
  )
}

const Counter = Object.assign(CounterRoot, { Input: CounterInput })

export { Counter }
